import { type MutableRefObject, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIframePoolStore } from "@/components/Canvas/IframePool/iframePoolStore";
import { useIframeStore } from "@/components/Canvas/stores/iframeStore";
import { revertCursorType } from "@/store/globalSettingsSlice";
import {
  FIXED_IFRAME_MESSAGE_IDS,
  setMessageResponse,
} from "@/store/iframeSlice";
import {
  type DevErrorJson,
  useDevServerStore,
} from "@/store/zustand/devServerStore";
import { track } from "@/utils/events/tracker";
import { extractApplicationStoryboardUrl } from "@/utils/iframeUtils";
import { handlePreviewError } from "@/utils/imageUtils";
import { useUpdateProjectPreview } from "@/utils/projectPreviewUtils";
import type { Storyboard } from "@/utils/types";
import { updateCanvasStoryboards } from "@/store/supabaseSlice";
import {
  useActiveCanvas,
  useActiveProject,
  useAutoUpdateRef,
  useCanvasId,
} from "@/utils/utils";
import { useApplyInstantUpdates } from "../Storyboard/StaticIframe/hooks/useApplyInstantUpdates";
import _ from "lodash";
import { updateAllStoryboardIdsInNavTree } from "@/utils/navTreeUtils";

export const trackError = _.debounce(
  (event: "STORYBOARD_ERROR" | "STORYBOARD_SKIP_ERROR", payload: any) => {
    track(event, payload);
  },
  100,
  { leading: true, trailing: true, maxWait: 2000 },
);

export const handleErrorFromIframe = (
  error: DevErrorJson,
  sbIds?: string[],
  isConsoleError?: boolean,
) => {
  const messagesToSkip = [
    "Uncaught TypeError: Cannot read properties of null (reading 'useContext')",
    "Uncaught TypeError: Cannot read properties of null (reading 'useState')",
    "Uncaught TypeError: Cannot read properties of null (reading 'useMemo')",
    "Uncaught TypeError: Cannot read properties of null (reading 'useRef')",
    "Uncaught SyntaxError: Unexpected token '<'",
    "data-tempoelementid",
  ];

  const skipErrorMessage = messagesToSkip.some((message) =>
    error?.message?.includes(message),
  );
  if (skipErrorMessage) {
    trackError("STORYBOARD_SKIP_ERROR", {
      storyboard_ids: sbIds,
      error: error,
      type: "compile",
    });
    return;
  }

  // Check if this is a console error and route it to terminal errors
  if (isConsoleError) {
    trackError("STORYBOARD_ERROR", {
      storyboard_ids: sbIds,
      error: error,
      type: "console",
    });
    useDevServerStore.getState().addTerminalErrors([error]);
    return;
  }

  trackError("STORYBOARD_ERROR", {
    storyboard_ids: sbIds,
    error: error,
    type: "compile",
  });

  if (sbIds) {
    sbIds.forEach((sbId) => {
      useDevServerStore.getState().addErrorForStoryboard(sbId, error);
    });
  } else {
    useDevServerStore.getState().addTerminalErrors([error]);
  }
};

/**
 * Hook to connect to and handle all post messaging to iframes
 */
export const useIframePostMessaging = (
  canvasRef?: MutableRefObject<any>,
  iframesRef?: MutableRefObject<{ [storyboardId: string]: any }>,
) => {
  const dispatch = useDispatch<any>();

  const canvasId = useCanvasId();

  const canvasIdRef = useAutoUpdateRef(canvasId);

  const showModal = useSelector((state: any) => state.globalSettings.showModal);
  const setIframeMessagingPort = useIframeStore(
    (store) => store.setIframeMessagingPort,
  );
  const setPortForApplicationStoryboard = useIframeStore(
    (store) => store.setPortForApplicationStoryboard,
  );

  const cursorType = useSelector(
    (state: any) => state.globalSettings.cursorType,
  );
  const cursorTypeRef = useAutoUpdateRef(cursorType);

  useEffect(() => {
    // Clear the main iframe port when the canvasId changes
    setIframeMessagingPort(null);
  }, [canvasId]);

  const project = useActiveProject();
  const projectRef = useAutoUpdateRef(project);

  const cursorTypeFromKeyDown = useSelector(
    (state: any) => state.globalSettings.cursorTypeFromKeyDown,
  );

  const cursorTypeFromKeyDownRef = useAutoUpdateRef(cursorTypeFromKeyDown);

  const activeCanvas = useActiveCanvas();
  const activeCanvasRef = useAutoUpdateRef(activeCanvas);

  const canvasIdToDevServerUrl = useSelector(
    (state: any) => state.iframe.canvasIdToDevServerUrl,
  );
  const canvasIdToDevServerUrlRef = useAutoUpdateRef(canvasIdToDevServerUrl);

  const onIframeMessageInternal = useCallback(
    (sbId: any, e: any) => {
      // Callback for when a message is received from an iframe
      if (e.data.id === FIXED_IFRAME_MESSAGE_IDS.COMPONENT_PREVIEW_ERROR) {
        const { componentKey: errorComponentKey, error } = e.data;
        handlePreviewError(errorComponentKey, error);
      } else if (e.data.id === FIXED_IFRAME_MESSAGE_IDS.ERROR) {
        const storyboard = activeCanvasRef.current?.storyboards?.find(
          (sb: Storyboard) => sb.id === sbId,
        );
        if (!storyboard || storyboard?.deleted) {
          return;
        }

        if (e.data.error) {
          handleErrorFromIframe(e.data.error, [sbId], e.data.isConsoleError);
        }
      } else if (e.data.id === FIXED_IFRAME_MESSAGE_IDS.HEARTBEAT) {
        useIframePoolStore.getState().handleHeartbeatReceived(sbId);
        return; // Don't dispatch heartbeat messages to general message response
      } else if (e.data.id === FIXED_IFRAME_MESSAGE_IDS.LATEST_HREF) {
        updateCanvasStoryboards(canvasIdRef.current, [
          {
            changeType: "update",
            storyboardId: sbId,
            pageId: "", // Will be resolved by updateCanvasStoryboards
            storyboardUpdateDict: {
              route: extractApplicationStoryboardUrl(
                e.data.href,
                canvasIdToDevServerUrlRef.current[canvasIdRef.current],
              ),
            },
          },
        ]);
        return;
      }

      dispatch(
        setMessageResponse({
          ...e.data,
          storyboardId: sbId,
          canvasId: canvasIdRef.current,
        }),
      );
    },
    [activeCanvasRef, canvasIdRef, dispatch, canvasIdToDevServerUrlRef],
  );

  const onIframeMessageInternalRef = useAutoUpdateRef(onIframeMessageInternal);

  const { applyInstantUpdate } = useApplyInstantUpdates();

  const updateProjectPreview = useUpdateProjectPreview();

  /**
   * Triggered when either of the iframes have finished loading
   * @param contentWindow the window proxy of the iframe that triggered this function
   * @param applicationStoryboardInfo the application storyboard info if the iframe is an application storyboard
   * @returns
   */
  const onLoadIFrame = useCallback(
    async (
      contentWindow: WindowProxy,
      applicationStoryboardInfo?: {
        storyboardId: string;
        type: string;
        fullFilePath: string;
        originalStoryboardUrl: string;
        iframeId: string; // Only for application storyboards
      },
    ) => {
      // Connect post message channel to this iframe
      const channel = new MessageChannel();
      const port1 = channel.port1;

      setTimeout(() => {
        // Set the port appropriately
        if (applicationStoryboardInfo) {
          setPortForApplicationStoryboard(
            applicationStoryboardInfo.iframeId,
            port1,
          );
        } else {
          setIframeMessagingPort(port1);
        }

        port1.onmessage = (e: any) => {
          // Use the storyboard ID from the message
          // But for application storyboards they do not change, so keep the same one
          // Handle heartbeats first - they don't need currentStoryboardId
          if (e.data.id === FIXED_IFRAME_MESSAGE_IDS.HEARTBEAT) {
            const poolState = useIframePoolStore.getState();
            // For app storyboards, pass the iframeId; for main iframe, no argument
            if (applicationStoryboardInfo) {
              poolState.handleHeartbeatReceived(
                applicationStoryboardInfo.iframeId,
              );
            } else {
              poolState.handleHeartbeatReceived();
            }
            return; // Don't process heartbeat through normal handler
          }

          let currentStoryboardId: string | undefined;
          if (applicationStoryboardInfo) {
            currentStoryboardId = applicationStoryboardInfo.storyboardId;
          } else if (e.data.fileKey) {
            currentStoryboardId = Object.values(
              useIframePoolStore.getState().registeredStoryboards,
            ).find((sb) => sb.fileKey === e.data.fileKey)?.id;
          }

          if (!currentStoryboardId) {
            console.error(
              `[IFRAME POST MESSAGING] No storyboard found for file key: ${e.data.fileKey}`,
            );
            return;
          }

          // For iframe pool - find which storyboard this iframe is currently serving
          const poolState = useIframePoolStore.getState();

          const storyboard = activeCanvasRef.current?.storyboards?.find(
            (sb: Storyboard) => sb.id === currentStoryboardId,
          );

          if (storyboard && e.data.id === FIXED_IFRAME_MESSAGE_IDS.NAV_TREE) {
            updateAllStoryboardIdsInNavTree(
              e.data.navTree,
              currentStoryboardId,
            );
            poolState.handleNavTreeReceived(
              storyboard,
              e.data.shellHtml,
              e.data.bodyHtml,
              canvasIdRef.current,
              applyInstantUpdate,
              e.data.navTree,
              e.data.overwriteHotReloadTs,
            );
          } else if (
            storyboard &&
            e.data.id === FIXED_IFRAME_MESSAGE_IDS.NEW_OUTER_HTML_ONLY
          ) {
            poolState.handleNewOuterHtmlOnly(
              storyboard,
              e.data.shellHtml,
              e.data.bodyHtml,
              canvasIdRef.current,
              applyInstantUpdate,
              e.data.overwriteHotReloadTs,
            );
          } else if (e.data.id === FIXED_IFRAME_MESSAGE_IDS.SCROLL_POSITION) {
            poolState.setStoryboardScrollPosition(
              currentStoryboardId,
              e.data.domUniquePath,
              e.data.scrollTop || 0,
              e.data.scrollLeft || 0,
            );
          } else if (
            e.data?.id === FIXED_IFRAME_MESSAGE_IDS.COMPONENT_PREVIEW_CAPTURED
          ) {
            const { imageData } = e.data;
            if (projectRef.current) {
              updateProjectPreview(projectRef.current.id, imageData);
            }
          } else if (
            e.data?.id === FIXED_IFRAME_MESSAGE_IDS.COMPONENT_PREVIEW_ERROR
          ) {
            console.error(
              `[PREVIEW UPLOAD] Failed to generate preview for ${currentStoryboardId}:`,
              e.data.error,
            );
          }

          // Also process through normal handler for other message types
          onIframeMessageInternalRef.current(currentStoryboardId, e);
        };

        // Transfer port2 to the iframe
        contentWindow.postMessage("init", "*", [channel.port2]);

        // Whenenver a new iframe is loaded switch back to non-drive mode if it was triggered from keypress
        if (
          cursorTypeRef.current === "drive" &&
          cursorTypeFromKeyDownRef.current
        ) {
          dispatch(revertCursorType());
        }

        port1.postMessage({
          id: "REGISTER_EVENT_LISTENERS",
          type: "executeFunction",
          payload: {
            functionName: "initProject",
            args: [
              applicationStoryboardInfo?.storyboardId || null,
              projectRef?.current?.framework,
            ],
          },
        });
      }, 100);

      if (applicationStoryboardInfo) {
        setTimeout(() => {
          port1.postMessage({
            id: "CAPTURE_PROJECT_PREVIEW",
            type: "executeFunction",
            payload: {
              functionName: "captureProjectPreview",
            },
          });
        }, 5 * 1000);
      }
    },
    [showModal, project, canvasId],
  );

  return {
    onLoadIFrame,
    onIframeMessageInternal: onIframeMessageInternalRef.current,
  };
};
